import { useState, useEffect, useRef } from "react";
import { Col, Row, Overlay, Tooltip } from "react-bootstrap";
import GridCard from "../../../components/shared/gridCard/GridCard";
import Datatable from "../../../components/shared/datatable/Datatable";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import Oven from "../../../images/oven_green.svg";
import GreenRoomOvenModal from "./GreenRoomOvenModal";
import GreenRoomSortingModal from "./GreenRoomSortingModal";
import GreenRoomStatusModal from "./GreenRoomStatusModal";
import {
  useGetGreenRoomSortingRecordQuery,
  useGetGreenRoomSummaryQuery,
  useSearchGreenRoomOvenDataQuery,
  useSearchGreenRoomLogsQuery,
} from "./greenRoomApi";
import Box from "../../../images/boxes.svg";
import CoolingRoom from "../../../images/cooling_room_orange.svg";
import { DEFAULT_DATETIME_FORMAT, DEFAULT_TIME_FORMAT } from "../../../helpers/constants";
import {
  convertToUserTimezone,
  formatDateAndTime,
  getCurrentDate,
  makePriorLotNumber,
} from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import RewarkPackedDataModal from "../rework/RewarkPackedDataModal";
import {
  useGetCheekStripPackedDataForReworkQuery,
  useProcessAccumulatedDataMutation,
} from "../rework/reworkApi";

const GreenRoom = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addRecord, setAddRecord] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tableFilters, setTableFilters] = useState({ type: "all", status: "accumulated", entered_date: "" });
  const [page, setPage] = useState(1);
  const [ovenFilter, setOvenFilter] = useState({});
  const [tablePage, setTablePage] = useState(1);
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [activeBaggedTooltip, setActiveBaggedTooltip] = useState(null);
  const [isHoveringBaggedTooltip, setIsHoveringBaggedTooltip] = useState(false);
  const baggedTooltipRefs = useRef({});
  const [selectedBox, setSelectedBox] = useState(null);
  const [boxForm, setBoxForm] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState(null);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));
  const [packingBoxTab, setPackingBoxTab] = useState("cheeks");
  const [addPackingData, setAddPackingData] = useState(false);
  const [packingModalMode, setPackingModalMode] = useState("");
  const [packingRecord, setPackingRecord] = useState(null);
  const [showPackingModal, setShowPackingModal] = useState(false);

  const reworkGreenLogLimit = 5;
  const reworkGreenOvenLimit = 10;

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);

  const { data: sortingRecordData } = useGetGreenRoomSortingRecordQuery({ date });
  const { data: summaryData } = useGetGreenRoomSummaryQuery({ date });
  const { data: greenRoomLogsData, isLoading: isGreenRoomLogsLoading, isFetching: isGreenRoomLogsFetching } = useSearchGreenRoomLogsQuery({
    page: tablePage, per_page: reworkGreenLogLimit, ...tableFilters,
  });
  // const { data: ovenData, isLoading: isOvenLoading, isFetching: isOvenFetching } = useSearchGreenRoomOvenDataQuery({
  //   page, data_from: "rework", per_page: reworkGreenOvenLimit, ...ovenFilter,
  // });
  const { data: packedData } = useGetCheekStripPackedDataForReworkQuery(
    { date, data_source: "rework", data_type: "green" },
    { refetchOnMountOrArgChange: true }
  );
  const [processAccumulatedData, { isLoading: isProcessing }] = useProcessAccumulatedDataMutation();

  const isToday = date === currentDate;
  const cheeksRecord = packedData?.data?.data?.find((item) => item.type === "cheeks" && item?.id);
  const stripsRecord = packedData?.data?.data?.find((item) => item.type === "strips" && item?.id);
  const hasCheeksRecord = Boolean(cheeksRecord);
  const hasStripsRecord = Boolean(stripsRecord);

  const cheeksBoxesPackedData = packedData?.data?.data
    ?.filter((item) => item.type === "cheeks")
    .map((item) => [
      { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
      { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
      { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("tapita_packed_before_lunch_20_pound", "Tapita packed before lunch"), value: item?.tapita_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("tapita_packed_after_lunch_20_pound", "Tapita packed after lunch"), value: item?.tapita_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("total_cheek_boxes_packed", "Total Cheek Boxes Packed"), value: item.boxes_packed },
      { label: _t("total_cheek_pallets_ready", "Total Cheek Pallets Ready"), value: item.pallets },
    ]);

  const stripsBoxesPackedData = packedData?.data?.data
    ?.filter((item) => item.type === "strips")
    .map((item) => [
      { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
      { label: _t("total_strip_boxes_packed", "Total Strip Boxes Packed"), value: item.boxes_packed },
      { label: _t("total_strip_pallets_ready", "Total Strip Pallets Ready"), value: item.pallets },
    ]);

  const handleAddData = () => { setAddRecord(true); setModalMode("add"); setSelectedRecord(null); setShowModal(true); };
  const handleEdit = (data) => { setAddRecord(true); setModalMode(data.status === "cart_unloaded" ? "view" : "edit"); setSelectedRecord(data); setShowModal(true); };
  const handlePageChange = (newPage) => setPage(newPage);
  const handleFilterApply = (newFilters) => { setTableFilters((prev) => ({ ...prev, ...newFilters })); setTablePage(1); };
  const handleOpenBoxModal = (box) => { setSelectedBox(box); setBoxForm(box ? { ...box } : null); setShowBoxModal(true); };
  const handleProcessAll = async () => {
    try { await processAccumulatedData({ entity_type: "green" }).unwrap(); }
    catch (error) { console.error("Error processing accumulated data:", error); }
  };
  const handleCheeksPacked = () => {
    if (!isToday && hasCheeksRecord) return;
    setAddPackingData(true);
    setPackingModalMode(hasCheeksRecord ? "editCheeks" : "cheeks");
    setPackingRecord(hasCheeksRecord ? cheeksRecord : null);
    setShowPackingModal(true);
  };
  const handleStripsPacked = () => {
    if (!isToday && hasStripsRecord) return;
    setAddPackingData(true);
    setPackingModalMode(hasStripsRecord ? "editStrips" : "strips");
    setPackingRecord(hasStripsRecord ? stripsRecord : null);
    setShowPackingModal(true);
  };

  const listItems = [
    { label: _t("total_green_room_cheeks_kg", "Total Green Room Cheeks Kg"), value: summaryData?.data?.total_kg_cheeks_in_green_room || 0, icon: Oven, showDay: false },
    { label: _t("total_green_room_strips_kg", "Total Green Room Strips Kg"), value: summaryData?.data?.total_kg_strips_in_green_room || 0, icon: Oven, showDay: false },
    // { label: _t("processing.totalCartsInOven", "Total Carts in Oven"), value: summaryData?.data?.total_carts_in_ovens || 0, icon: Oven, showDay: false },
    // { label: _t("processing.cartsInCoolingRoom", "Carts in Cooling Room"), value: summaryData?.data?.total_carts_in_cooling_room || 0, icon: CoolingRoom, showDay: false },
    // { label: _t("total_cheeks_kg_bagged", "Total Cheeks Kg Bagged"), value: summaryData?.data?.total_cheeks_kg_bagged || 0, icon: Box, showDay: false },
    // { label: _t("total_strips_kg_bagged", "Total Strips Kg Bagged"), value: summaryData?.data?.total_strips_kg_bagged || 0, icon: Box, showDay: false },
    // { label: _t("total_tapita_kg_bagged", "Total Tapita Kg Bagged"), value: summaryData?.data?.total_tapita_kg_bagged || 0, icon: Box, showDay: false },
  ];

  const greenRoomLogColumns = [
    { label: _t("humidity_table_date", "Date"), accessor: "created_at", cell: ({ value }) => convertToUserTimezone(value) },
    { label: _t("lot_no", "Lot Number"), accessor: "lot_number_id", cell: ({ value }) => value ? `#${value}` : "" },
    { label: _t("humidity_table_type", "Type"), accessor: "type", cell: ({ value }) => value === "cheeks" ? _t("type_cheeks", "Cheeks") : _t("type_strips", "Strips") },
    { label: _t("total_kg", "Total Kg"), accessor: "total_kg" },
    {
      label: _t("humidity_table_status", "Status"), accessor: "rework_status",
      cell: ({ row }) => (
        <span className={row.rework_status === "processed" ? "cmn_badge" : "cmn_badge inactive"}>
          {row.rework_status === "processed" ? _t("processed", "Processed") : _t("accumulated", "Accumulated")}
        </span>
      ),
    },
    // {
    //   label: _t("edit", "Edit"), accessor: "id",
    //   cell: ({ row }) => (
    //     <Button
    //       size="small"
    //       label={row.rework_status === "processed" ? _t("processed", "Processed") : _t("edit", "Edit")}
    //       onClick={() => { setStatusForm({ ...row, created_at: convertToUserTimezone(row.created_at) }); setShowStatusModal(true); }}
    //       className={row.rework_status === "processed" ? "borderRedBtn" : ""}
    //       disabled={row.rework_status === "processed"}
    //     />
    //   ),
    // },
  ];

  const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const diffMs = new Date(end) - new Date(start);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `${Math.floor(diffMinutes / 60)} h ${diffMinutes % 60} min`;
  };

  // const ovenColumns = [
  //   { label: _t("date", "Date"), accessor: "date_time_entered", cell: ({ value }) => convertToUserTimezone(value) },
  //   { label: _t("ovenNumber", "Oven Number"), accessor: "oven", cell: ({ value }) => value?.name || "" },
  //   { label: _t("time", "Time"), accessor: "time_entered", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_TIME_FORMAT) : "" },
  //   { label: _t("cartNumber", "Carts Number"), accessor: "cart_number", cell: ({ value }) => value?.name || "" },
  //   { label: _t("type", "Type"), accessor: "type", cell: ({ value }) => value === "cheeks_tapita" ? "cheeks/tapita" : value },
  //   { label: _t("treysCount", "Treys Count"), accessor: "treys_count" },
  //   {
  //     label: _t("status", "Status"), accessor: "status",
  //     cell: ({ row }) => {
  //       const { status, time_end } = row;
  //       const isOvenDone = status === "cooking" && time_end &&
  //         convertToUserTimezone(time_end, null, undefined, "valueOf") < convertToUserTimezone(null, null, undefined, "valueOf");
  //       return (
  //         <span className={isOvenDone ? "cmn_badge done" : status === "cart_unloaded" ? "cmn_badge done" : status === "cooling_room" ? "cmn_badge" : status === "cooking" ? "cmn_badge inactive" : "cmn_badge rejected"}>
  //           {isOvenDone ? _t("ovenDone", "Oven Done!") : status === "cooling_room" ? _t("coolingRoom", "Cooling Room") : status === "cooking" ? _t("cooking", "Cooking") : status === "cart_unloaded" ? _t("cart_unloaded", "Cart Unloaded") : _t("bagged", "Bagged")}
  //         </span>
  //       );
  //     },
  //   },
  //   { label: _t("ovenDuration", "Oven Duration"), accessor: "duration", cell: ({ row }) => calculateDuration(row.time_entered, row.time_end) },
  //   { label: _t("timeEnd", "Time End"), accessor: "time_end", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" },
  //   {
  //     label: _t("bagged_weight", "Bagged Weight"), accessor: "status",
  //     cell: ({ row }) => {
  //       if (row.status !== "cart_unloaded") return <span style={{ color: "#ccc" }}>—</span>;
  //       const { type, cheeks_kg_bagged, strips_kg_bagged, tapita_kg_bagged } = row;
  //       const allBagged = [[_t("cheeks", "Cheeks"), cheeks_kg_bagged], [_t("strips", "Strips"), strips_kg_bagged], [_t("tapita", "Tapita"), tapita_kg_bagged]];
  //       const pairs =
  //         type === "cheeks" ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged]] :
  //         type === "strips" ? [[_t("strips", "Strips"), strips_kg_bagged]] :
  //         type === "tapita" ? [[_t("tapita", "Tapita"), tapita_kg_bagged]] :
  //         type === "cheeks/strips" ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged], [_t("strips", "Strips"), strips_kg_bagged]] :
  //         type === "cheeks_tapita" ? [[_t("cheeks", "Cheeks"), cheeks_kg_bagged], [_t("tapita", "Tapita"), tapita_kg_bagged]] :
  //         allBagged.filter(([, v]) => parseFloat(v) > 0);
  //       const total = pairs.reduce((sum, [, v]) => sum + (parseFloat(v) || 0), 0);
  //       if (!baggedTooltipRefs.current[row.id]) baggedTooltipRefs.current[row.id] = { current: null };
  //       return (
  //         <span className="d-flex align-items-center gap-1">
  //           <span
  //             ref={(el) => { if (!baggedTooltipRefs.current[row.id]) baggedTooltipRefs.current[row.id] = { current: null }; baggedTooltipRefs.current[row.id].current = el; }}
  //             style={{ background: "rgba(238,114,37,0.08)", border: "1px solid rgba(238,114,37,0.25)", borderRadius: "6px", padding: "2px 10px", fontSize: "12px", color: "#ee7225", fontWeight: 600, cursor: "pointer" }}
  //             onMouseEnter={() => setActiveBaggedTooltip(row.id)}
  //             onMouseLeave={() => setTimeout(() => { if (!isHoveringBaggedTooltip) setActiveBaggedTooltip(null); }, 100)}
  //             onClick={() => setActiveBaggedTooltip(activeBaggedTooltip === row.id ? null : row.id)}
  //           >{total} kg</span>
  //           <Overlay target={baggedTooltipRefs.current[row.id]?.current} show={activeBaggedTooltip === row.id} placement="top" rootClose onHide={() => { if (!isHoveringBaggedTooltip) setActiveBaggedTooltip(null); }}>
  //             {(props) => (
  //               <Tooltip id={`bagged-tooltip-${row.id}`} {...props} className="custom-tooltip" onMouseEnter={() => setIsHoveringBaggedTooltip(true)} onMouseLeave={() => { setIsHoveringBaggedTooltip(false); setActiveBaggedTooltip(null); }}>
  //                 <div className="tooltip-content">
  //                   <div className="tooltip-header">{_t("bagged_weight_breakdown", "Bagged Weight")}</div>
  //                   <ul className="tooltip-list">{pairs.map(([label, value]) => (<li key={label}><span>• {label}</span><span className="tooltip-amount">{parseFloat(value) || 0} kg</span></li>))}</ul>
  //                 </div>
  //               </Tooltip>
  //             )}
  //           </Overlay>
  //         </span>
  //       );
  //     },
  //   },
  //   {
  //     label: _t("humidity_table_overview", "Overview"), accessor: "status",
  //     cell: ({ row }) => row.status === "cart_unloaded" ? null : <Button size="small" label={_t("edit", "Edit")} onClick={() => handleEdit(row)} />,
  //   },
  // ];

  return (
    <div>
      {addRecord && (
        <GreenRoomOvenModal show={showModal} setShow={setAddRecord} mode={modalMode} record={selectedRecord} type_name={tableFilters.type === "all" ? "" : tableFilters.type} date={date} />
      )}
      {showBoxModal && (
        <GreenRoomSortingModal show={showBoxModal} setShow={setShowBoxModal} record={boxForm} mode={boxForm?.id ? "edit" : "add"} />
      )}
      {addPackingData && (
        <RewarkPackedDataModal show={showPackingModal} setShow={setAddPackingData} mode={packingModalMode} record={packingRecord} lot_number_id={lotNumber} data_source="rework" data_type="green" />
      )}
      {showStatusModal && (
        <GreenRoomStatusModal show={showStatusModal} setShow={setShowStatusModal} statusForm={statusForm} setStatusForm={setStatusForm} />
      )}

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("overview_green_room", "Green Room Overview")}</h6>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard key={index} title={list.label} value={list.value} icon={list.icon} day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)} showDay={list.showDay} />
          ))}
        </ul>
      </div>

      <div className="my-3" />
      <div className="humid_logs h-100 pt-2">
        <Datatable
          title={_t("green_rework_log", "Green Rework Logs")}
          columns={greenRoomLogColumns}
          tableData={greenRoomLogsData?.data}
          isLoading={isGreenRoomLogsLoading || isGreenRoomLogsFetching}
          showPegination={true}
          page={tablePage}
          onPageChange={setTablePage}
          showCount={true}
          showFilter={handleFilterApply}
          showFilterButton={true}
          filterModule="green-room-logs-filter"
          filters={tableFilters}
          pagination
          pageLimit={reworkGreenLogLimit}
          totalRecordsTitle={_t("number_of_green_room_logs", "Number of Green Room Logs")}
          buttons={[
            {
              label: isProcessing ? _t("processing", "Processing...") : _t("process_all_accumulated", "Process All Accumulated"),
              onClick: handleProcessAll,
            },
          ]}
        />
      </div>

    {/* 
      <div className="my-3" />
      {(isOvenLoading || isOvenFetching) && <div className="loader-container"><div className="loader"></div></div>}
      {!isOvenLoading && !isOvenFetching && (
        <div className="humid_logs h-100 pt-2">
          <Datatable
            title={_t("green_oven_log", "Green Oven Logs")}
            columns={ovenColumns}
            tableData={ovenData?.data}
            buttons={[{ label: _t("add_oven_data", "Add Oven Data"), onClick: handleAddData, icon: PlusSvg }]}
            showPegination={true}
            pagination
            page={page}
            onPageChange={handlePageChange}
            showCount={true}
            showFilter={setOvenFilter}
            showFilterButton={true}
            filterModule="oven-filter"
            filters={ovenFilter}
            pageLimit={reworkGreenOvenLimit}
          />
        </div>
      )} */}

      {/* SORTING & PACKING BOXES */}
      <div className="my-3" />
      <Row className="g-3">
        <Col lg={6}>
          <div className="dash_card h-100">
            <div className="d-flex justify-content-between align-items-center dash_header">
              <h3 className="mb-0">{_t("cheeks_strips_sorting_data", "Cheeks/Strip Sorting Data")}</h3>
              <Button size="xs" label={sortingRecordData?.data?.id ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")} icon={PlusSvg} onClick={() => handleOpenBoxModal(sortingRecordData?.data?.id ? sortingRecordData.data : null)} />
            </div>
            <div className="p-3">
              <Row>
                {sortingRecordData?.data?.id ? (() => {
                  const box = sortingRecordData.data;
                  return (
                    <>
                      <Col lg={6}><InfoCard label={_t("prime_cheeks_kg", "Prime Cheeks Kg")} value={box.prime_cheeks_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("second_cheeks_kg", "Second Cheeks Kg")} value={box.second_cheeks_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("prime_strips_kg", "Prime Strips Kg")} value={box.prime_strips_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("second_strips_kg", "Second Strips Kg")} value={box.second_strips_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("tapita_kg", "Tapita Kg")} value={box.tapita_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("pieces_kg", "Pieces Kg")} value={box.pieces_kg} /></Col>
                      <Col lg={6}><InfoCard label={_t("trash", "Trash")} value={box.rejected_kg} /></Col>
                    </>
                  );
                })() : <NoDataFound />}
              </Row>
            </div>
          </div>
        </Col>

        {/* Packing Box */}
        <Col lg={6}>
          <div className="dash_card h-100">
            <div className="d-flex justify-content-between align-items-center dash_header">
              <h3 className="mb-0">{_t("rework_packing_box_logs", "Packing Logs")}</h3>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex" style={{ background: "rgba(238,114,37,0.08)", borderRadius: "6px", padding: "2px" }}>
                  <button onClick={() => setPackingBoxTab("cheeks")} style={{ border: "none", borderRadius: "5px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, cursor: "pointer", background: packingBoxTab === "cheeks" ? "#ee7225" : "transparent", color: packingBoxTab === "cheeks" ? "#fff" : "#ee7225", transition: "all 0.15s" }}>
                    {_t("type_cheeks", "Cheeks")}
                  </button>
                  <button onClick={() => setPackingBoxTab("strips")} style={{ border: "none", borderRadius: "5px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, cursor: "pointer", background: packingBoxTab === "strips" ? "#ee7225" : "transparent", color: packingBoxTab === "strips" ? "#fff" : "#ee7225", transition: "all 0.15s" }}>
                    {_t("type_strips", "Strips")}
                  </button>
                </div>
                {packingBoxTab === "cheeks" ? (
                  (isToday || !hasCheeksRecord) && <Button size="xs" label={hasCheeksRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")} icon={PlusSvg} onClick={handleCheeksPacked} />
                ) : (
                  (isToday || !hasStripsRecord) && <Button size="xs" label={hasStripsRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")} icon={PlusSvg} onClick={handleStripsPacked} />
                )}
              </div>
            </div>
            <div className="p-3">
              {packingBoxTab === "cheeks" ? (
                <Row>
                  {cheeksBoxesPackedData?.length > 0 ? cheeksBoxesPackedData.map((data, index) => (
                    <Row key={index}>{data.map((item, i) => <Col lg={6} key={`${item.label}-${i}`}><InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} /></Col>)}</Row>
                  )) : <NoDataFound />}
                </Row>
              ) : (
                <Row>
                  {stripsBoxesPackedData?.length > 0 ? stripsBoxesPackedData.map((data, index) => (
                    <Row key={index}>{data.map((item, i) => <Col lg={6} key={`${item.label}-${i}`}><InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} /></Col>)}</Row>
                  )) : <NoDataFound />}
                </Row>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default GreenRoom;
